import React, { FocusEventHandler } from "react"
import { RequiredSymbol } from "./various"
import { InputError } from "./inputError"
import { debounce } from "lodash";
import { AbstractStore } from "../AbstractStores";
import { DropdownList } from "./dropdownList";

interface TextInputHintsProps {
	name?: string;
	error?: string;
	list: string[];
	label?: string;
	value: string;
	onChange: (description: string) => void;
	onSelect: (item: string | null) => void;
	style?: React.CSSProperties;
	busy?: boolean;
	required?: boolean;
	readOnly?: boolean;
	onFocus?: FocusEventHandler;
	onBlur?: FocusEventHandler;
	onEnter?: () => boolean;
}


interface InternalState {
	isOpen: boolean | null;
	focused: boolean;
}

class TextInputHintsInternal extends React.Component<TextInputHintsProps, InternalState>
{
	private input = React.createRef<HTMLInputElement>();
	private container = React.createRef<HTMLDivElement>();

	constructor(props: TextInputHintsProps) {
		super(props);
		this.state = {
			isOpen: null,
			focused: false,
		};
	}

	private onChange = (event: React.FormEvent<HTMLInputElement>) => {
		this.props.onChange((event.target as any).value);
	}

	componentDidMount() {
		this.container.current!.addEventListener("keydown", this.handleKeyDown, false);
	}

	componentWillUnmount() {
		this.container.current!.removeEventListener("keydown", this.handleKeyDown, false);
	}

	public focus = () => {
		this.input.current!.focus();
	}

	handleKeyDown = (e: KeyboardEvent) => {
		switch (e.key) {
			case "ArrowUp":
				this.dropDownMenu.current?.moveUp();
				break;
			case "ArrowDown":
				this.dropDownMenu.current?.moveDown();
				break;
			case "Enter":
				this.dropDownMenu.current?.select();
				if (this.props.onEnter?.()) {
					e.preventDefault();
				}
				break;
			case "Tab":
				this.dropDownMenu.current?.select();
				break;
		}
	}

	public toggle = () => {
		this.setState({ isOpen: !this.state.isOpen });
	}

	private onFocus = (event: React.FocusEvent) => {
		this.props.onFocus?.(event);
		this.setState({ focused: true });
		if (!this.state.isOpen)
			this.setState({ isOpen: true });
	}

	private onBlur = (event: React.FocusEvent) => {
		this.props.onBlur?.(event);
		this.setState({ focused: false });
		if (this.state.isOpen) {
			setTimeout(() => this.setState({ isOpen: false }), 300);
		}
	}

	private onClick = () => {
		if (this.input.current === document.activeElement && !this.state.isOpen)
			this.setState({ isOpen: true });
	}


	private onSelect = (v: string | null) => {
		if (v == null) {
			this.props.onSelect(null);
		} else {
			const index = this.props.list.indexOf(v);
			if (index > -1) {
				const value = this.props.list[index];
				this.props.onSelect(value);
			}
			this.setState({ isOpen: false });
		}
	};

	private dropDownMenu = React.createRef<DropdownList<string,{}>>();
	render() {

		const divStyle = { ...(this.props.style || {}), width: "100%" };

		var required = this.props.required ? <RequiredSymbol /> : undefined;

		return (
			<div>
				{this.props.label && <label className="form-label" htmlFor={this.props.name} onClick={() => this.focus()}>{this.props.label}</label>} {required}
				<div
					ref={this.container}
					className={`form-control bncombo ${this.state.focused ? "focused" : ""}`}
					style={divStyle}>
					<input
						type="text"
						style={{ width: "100%", border: "none" }}
						name={this.props.name}
						ref={this.input}
						value={this.props.value}
						onChange={this.onChange}
						onFocus={this.onFocus}
						onBlur={this.onBlur}
						onClick={this.onClick}
						autoComplete="chrome-off"
						readOnly={this.props.readOnly}
						required={this.props.required}
					/>
					{!(this.props.required || !this.props.value || this.props.readOnly) && <a onClick={() => this.onSelect(null)}
						title="Rimuovi selezione"
						style={{ width: 10, marginRight: 20, textAlign: "center", cursor: "pointer" }}>
						<i className="fas fa-times text-danger" />
					</a>}
					{
						this.props.busy ?
							<div style={{ width: 10, marginRight: 10, textAlign: "center", cursor: "pointer" }}>
								<i title="Caricamento in corso" className="fas fa-sync fa-spin" />
							</div>
							: null
					}
				</div>
				<DropdownList<string,{}>
					ref={this.dropDownMenu}
					items={this.props.list}
					itemComponent={x => x}
					onSelect={this.onSelect}
					isOpen={this.state.isOpen ?? false}
				/>
				{this.props.children}
				<InputError error={this.props.error} />
			</div >
		);
	}
}


class TextInputHintsStore<T> extends AbstractStore<T> {
	searchedLocalities: { [index: string]: T[] } = {};
	constructor(idSelector: (item: T) => string) {
		super(idSelector);
	}

	getSearchResult(search: string) {
		return this.searchedLocalities[search.toLowerCase()] ?? [];
	}
}


interface Props {
	error?: string;
	onChange: (item: string | null) => void;
	hidden?: boolean;
	name?: string;
	placeholder?: string;
	label?: string;
	style?: React.CSSProperties;
	readOnly?: boolean;
	value?: string;
	required?: boolean;
	onFocus?: FocusEventHandler;
	onBlur?: FocusEventHandler;
	onEnter?: () => boolean;
	ActionsGet: (id: string) => Promise<string>;
	ActionsSearch: (literal: string) => Promise<string[]>;
}
interface State {
	busy: boolean;
	data: string[];
}

export class TextInputHints extends React.Component<Props, State> {

	private store: TextInputHintsStore<string>;

	private debouncedSearch: (searchValue: string) => void;
	constructor(props: Props) {
		super(props);
		this.store = new TextInputHintsStore<string>(x => x.toString() ?? null);
		this.debouncedSearch = debounce(this.search, 300);
		this.state = {
			busy: false,
			data: [],
		};
	}

	private listener = () => {
		if (this.props.value) {
			this.setState({
				data: this.store.getSearchResult(this.props.value),
				busy: false,
			});
		} else {
			this.setState({
				data: [],
				busy: false,
			});
		}
	}

	private SearchItems = async (literal: string) => {
		const data = await this.props.ActionsSearch(literal);
		this.store.searchedLocalities[literal.toLowerCase()] = data;
		this.store.updateOrAddAll(data);
		this.listener();
	}


	private search = (text: string) => {
		var data: string[] = [];
		let busy = false;
		if (text !== "") {
			data = this.store.getSearchResult(text);
			if (data.length === 0) {
				busy = true;
				this.SearchItems(text);
			}
		}
		this.setState({
			busy,
			data,
		});
	}

	private onChange = (text: string) => {
		this.debouncedSearch(text);
		this.props.onChange(text)
	}

	private onSelect = (item: string | null) => {
		this.setState({
			data: [],
			busy: false,
		});
		this.props.onChange(item);
	}

	render() {
		var component = (
			<TextInputHintsInternal
				style={this.props.style}
				name={this.props.name}
				label={this.props.label || "T"}
				value={this.props.value ?? ""}
				error={this.props.error}
				list={this.state.data}
				onChange={this.onChange}
				onSelect={this.onSelect}
				busy={this.state.busy}
				required={this.props.required}
				onBlur={this.props.onBlur}
				onFocus={this.props.onFocus}
				onEnter={this.props.onEnter}
			>
				{this.props.children}
			</TextInputHintsInternal>
		);
		if (this.props.hidden === true) {
			component = (<div />);
		}
		return component;
	}
}